import { Client, GraphError } from '@microsoft/microsoft-graph-client';
import * as Graph from '@microsoft/microsoft-graph-types';
import { z } from 'zod';
import { Column } from '../../../../sidecar/lists/lib/column';
import { List, Metadata } from '../../../../sidecar/lists/lib/list';
import {
  ColumnsFromList,
  FieldNamesFromList,
  ListCreateArguments,
  ListToResult
} from '../../../../sidecar/lists/lib/queries';
import { ArgumentSelector, IdSelector, Selector } from '../../../../sidecar/lists/lib/selector';

const log = console.log.bind(console);

const getListItems = async (
  client: Client,
  list: List,
  siteId: string,
  listId: string,
  listName: string,
  metadata: Metadata
) => {
  const columns = Object.entries(metadata.lists[listName].columns)
    // @ts-ignore
    .map(([, value]) => value.name)
    .join(',');

  try {
    const response = await client
      .api<Graph.ListItem[]>(`/sites/${siteId}/lists/${listId}/items`)
      .expand([`fields($select=${columns})`])
      .get();

    const rows = response.value.map(item => {
      const row: Record<string, any> = {
        id: item.id
      };

      if (!item.fields) throw new Error(`No fields for item ${item.id}`);
      const columns = Object.entries(metadata.lists[listName].columns);
      Object.entries(item.fields).forEach(([key, value]) => {
        // @ts-ignore
        const result = columns.find(([, column]) => {
          // @ts-ignore
          return column.name === key;
        });
        if (!result) return;
        const [columnName] = result;
        if (!list.columns[columnName]) {
          console.log(`No column for ${result}`);
          return;
        }
        const parser = list.columns[columnName].parse;
        row[columnName] = parser.parse(value);
      });

      return row;
    });
    return rows;
  } catch (e) {
    if (e instanceof GraphError && e.statusCode === 401) {
      return [];
    }
    throw e;
  }
};

const getListItem = async (
  client: Client,
  list: List,
  siteId: string,
  listId: string,
  listName: string,
  itemId: string,
  metadata: Metadata
) => {
  const columns = Object.entries(metadata.lists[listName].columns)
    // @ts-ignore
    .map(([, value]) => value.name)
    .join(',');

  const response = await client
    .api<Graph.ListItem>(`/sites/${siteId}/lists/${listId}/items/${itemId}`)
    .expand([`fields($select=${columns})`])
    .get();
  if (!response.id) throw new Error(`No id for item ${itemId}`);
  if (!response.fields) throw new Error(`No fields for item ${response.id}`);
  const row: Record<string, any> = {
    id: response.id
  };

  if (!response.fields) throw new Error(`No fields for item ${response.id}`);
  Object.entries(response.fields).forEach(([key, value]) => {
    const result = Object.entries(metadata.lists[listName].columns).find(
      // @ts-ignore
      ([, column]) => column.name === key
    );
    if (!result) return;
    const [columnName] = result;
    const parser = list.columns[columnName].parse;
    row[columnName] = parser.parse(value);
  });

  return row;
};

const executeMany = async <
  L extends List,
  Fields extends Column<string>[] | undefined = undefined,
  Result extends ListToResult<L, FieldNamesFromList<L, Fields>> = ListToResult<L, FieldNamesFromList<L, Fields>>
>(
  client: Client,
  list: L,
  metadata: Metadata,
  fields?: Fields,
  selector?: ArgumentSelector<L>
): Promise<Result[]> => {
  const listName = list.meta.id;
  const listId = metadata.lists[listName].id;
  const siteId = metadata.lists[listName].siteId;
  if (!metadata.lists[listName].columns) throw new Error(`No columns for list ${listName}`);

  if (!selector) {
    const rows = await getListItems(client, list, siteId, listId, listName, metadata);
    return rows as Result[];
  }

  const filteredFields = Object.keys(selector).filter(key => key !== 'id');
  const rows = await getListItems(client, list, siteId, listId, listName, metadata);
  return rows.filter(row => {
    return filteredFields.every(key => {
      // @ts-ignore
      const value = selector[key];
      return row[key] === value;
    });
  }) as Result[];
};

const executeOne = async <
  L extends List,
  Fields extends Column<string>[] | undefined = undefined,
  Result extends ListToResult<L, FieldNamesFromList<L, Fields>> = ListToResult<L, FieldNamesFromList<L, Fields>>
>(
  client: Client,
  list: L,
  metadata: Metadata,
  fields?: Fields,
  selector?: IdSelector
): Promise<Result> => {
  const listName = list.meta.id;
  const listId = metadata.lists[listName].id;
  const siteId = metadata.lists[listName].siteId;
  if (!metadata.lists[listName].columns) throw new Error(`No columns for list ${listName}`);

  if (!selector) {
    const rows = await getListItems(client, list, siteId, listId, listName, metadata);
    return rows[0] as Result;
  }

  const row = await getListItem(client, list, siteId, listId, listName, selector, metadata);
  return row as Result;
};

export const execute = async <
  L extends List,
  Fields extends Column<string>[] | undefined = undefined,
  S extends Selector<L> | undefined = undefined,
  Result extends ListToResult<L, FieldNamesFromList<L, Fields>> = ListToResult<L, FieldNamesFromList<L, Fields>>
>(
  client: Client,
  list: L,
  metadata: Metadata,
  fields?: Fields,
  selector?: S
): Promise<S extends IdSelector ? Result : Result[]> => {
  if (!selector) {
    return executeMany<L, Fields>(client, list, metadata, fields) as any;
  }

  if (typeof selector === 'string') {
    return executeOne<L, Fields>(client, list, metadata, fields, selector) as any;
  } else if (typeof selector === 'object') {
    return executeMany<L, Fields>(client, list, metadata, fields, selector) as any;
  } else {
    throw new Error(`Invalid selector ${selector}`);
  }
};

export class Lists {
  constructor(
    private client: Client,
    private metadata: Metadata
  ) {}

  enrichFields<L extends List>(list: L, fields: Record<string, any>) {
    const supportedFields = z.record(z.union([z.string(), z.array(z.string()), z.any()])).parse(fields);
    const enrichedFields = {};

    Object.entries(supportedFields).forEach(([key, value]) => {
      const result = Object.entries(this.metadata.lists[list.meta.id].columns).find(
        // @ts-ignore
        ([, column]) => column.name === key
      );
      if (!result) return;
      const [columnName] = result;
      const parser = list.columns[columnName].parse;
      // @ts-ignore
      enrichedFields[columnName] = parser.parse(value);
    }, {});

    return enrichedFields as ListToResult<L, FieldNamesFromList<L, undefined>>;
  }

  from<L extends List>(list: L) {
    const listName = list.meta.id;
    const listId = this.metadata.lists[listName].id;
    const siteId = this.metadata.lists[listName].siteId;
    const keys: Array<unknown> = [{ [list.meta.id]: `/sites/${siteId}/lists/${listId}/items` }];

    return {
      select: <Fields extends Column<string>[] | undefined = undefined>(fields?: Fields) => {
        return {
          keys,
          listId,
          siteId,
          where: <S extends Selector<L>>(selector: S) => {
            keys.push(selector);
            return {
              keys,
              listId,
              siteId,
              execute: () => execute<L, Fields, S>(this.client, list, this.metadata, fields, selector)
            };
          },
          execute: () => execute<L, Fields>(this.client, list, this.metadata, fields),

          join: <
            // If the parent select has had fields specified, then the key must be included in those fields
            // If not, it must be one of any of the fields from the list
            KeyField extends Fields extends Column<string>[] ? Fields[number] : ColumnsFromList<L>,
            // Another different, foreign list
            ForeignList extends List,
            // Optional fields to narrow down the select from the foreign list
            ForeignFields extends Column<string>[] | undefined = undefined
          >(
            keyField: KeyField,
            foreignList: ForeignList,
            foreignFields?: ForeignFields
          ) => {
            if (keyField.options?.type !== 'lookup')
              throw new Error(`Cannot join on non-lookup field ${keyField.name}`);

            const foreignListName = foreignList.meta.id;
            const foreignListId = this.metadata.lists[foreignListName].id;

            keys.push({ [foreignListName]: `/sites/${siteId}/lists/${foreignListId}/items` });

            return {
              keys,
              listId,
              siteId,

              where: <
                S extends Selector<L>,
                Main extends ListToResult<L, FieldNamesFromList<L, Fields>> = ListToResult<
                  L,
                  FieldNamesFromList<L, Fields>
                >,
                Foreign extends ListToResult<
                  ForeignList,
                  FieldNamesFromList<ForeignList, ForeignFields>
                > = ListToResult<ForeignList, FieldNamesFromList<ForeignList, ForeignFields>>,
                Result = Omit<Main, KeyField['name']> & Record<KeyField['name'], Foreign>
              >(
                selector: S
              ) => {
                log('select().from().join().where()');
                keys.push(selector);
                return {
                  keys,
                  listId,
                  siteId,

                  execute: async (): Promise<S extends IdSelector ? Result : Result[]> => {
                    if (typeof selector === 'string') {
                      const main = await executeOne<L, Fields>(this.client, list, this.metadata, fields, selector);
                      const foreign = await executeOne<ForeignList, ForeignFields>(
                        this.client,
                        foreignList,
                        this.metadata,
                        foreignFields,
                        selector
                      );
                      const result = main as Record<string, unknown>;
                      result[keyField.name] = foreign as Foreign;
                      return result as any;
                    } else if (typeof selector === 'object') {
                      const main = await executeMany<L, Fields>(this.client, list, this.metadata, fields, selector);
                      const foreign = await executeMany<ForeignList, ForeignFields>(
                        this.client,
                        foreignList,
                        this.metadata,
                        foreignFields
                      );

                      return main.map((item: Record<string, unknown>) => {
                        item[keyField.name] = foreign.find(f => f.id === item[keyField.name]);
                        return item;
                      }) as any;
                    } else {
                      throw new Error(`Invalid selector ${selector}`);
                    }
                  }
                };
              },
              execute: async () => {
                const main = await executeMany<L, Fields>(this.client, list, this.metadata, fields);
                const foreign = await executeMany<ForeignList, ForeignFields>(
                  this.client,
                  foreignList,
                  this.metadata,
                  foreignFields
                );

                type Main = ListToResult<L, FieldNamesFromList<L, Fields>>;
                type Foreign = ListToResult<ForeignList, FieldNamesFromList<ForeignList, ForeignFields>>;
                type Result = Omit<Main, KeyField['name']> & Record<KeyField['name'], Foreign>;

                return main.map((item: Record<string, unknown>) => {
                  item[keyField.name] = foreign.find(f => f.id === item[keyField.name]);
                  return item;
                }) as Result[];
              }
            };
          }
        };
      }
    };
  }

  update<L extends List>(list: L) {
    return {
      set: <Arguments extends ArgumentSelector<L>>(args: Arguments) => ({
        where: (selector: IdSelector) => {
          const listName = list.meta.id;
          const listId = this.metadata.lists[listName].id;
          const siteId = this.metadata.lists[listName].siteId;
          const keys: Array<unknown> = ['update', { [list.meta.id]: `/sites/${siteId}/lists/${listId}/items` }];
          const invalidatedKeys = [{ [list.meta.id]: `/sites/${siteId}/lists/${listId}/items` }];

          return {
            keys,
            invalidatedKeys,
            listId,
            siteId,
            execute: async () => {
              const body: Graph.FieldValueSet = {};

              const existing = await this.client
                .api<Graph.FieldValueSet>(`/sites/${siteId}/lists/${listId}/items/${selector}/fields`)
                .get();

              for (const key in this.metadata.lists[listName].columns) {
                const columnName = this.metadata.lists[listName].columns[key].name;
                const value = existing[columnName as keyof Graph.FieldValueSet];
                body[columnName as keyof Graph.FieldValueSet] = value;
              }

              for (const key in args) {
                const value = args[key];
                if (value === undefined) continue;
                const serialize = list.columns[key].serialize;
                const columnName = this.metadata.lists[listName].columns[key].name;
                body[columnName as keyof Graph.FieldValueSet] = serialize.parse(value);
              }

              await this.client.api(`/sites/${siteId}/lists/${listId}/items/${selector}/fields`).patch(body);
            }
          };
        }
      })
    };
  }

  insert<L extends List>(list: L) {
    const listName = list.meta.id;
    const listId = this.metadata.lists[listName].id;
    const siteId = this.metadata.lists[listName].siteId;
    const keys: Array<unknown> = ['insert', { [list.meta.id]: `/sites/${siteId}/lists/${listId}/items` }];
    const invalidatedKeys = [{ [list.meta.id]: `/sites/${siteId}/lists/${listId}/items` }];

    return {
      values: <Arguments extends ListCreateArguments<L>>(args: Arguments) => ({
        keys,
        invalidatedKeys,
        listId,
        siteId,
        execute: async () => {
          const body: Graph.FieldValueSet = {};

          for (const key in args) {
            const value = args[key];
            const columnName = this.metadata.lists[listName].columns[key]?.name;
            if (!columnName) throw new Error(`No column name for ${key}`);
            const serialize = list.columns[key].serialize;
            body[columnName as keyof Graph.FieldValueSet] = serialize.parse(value);
          }

          const response = await this.client
            .api<Graph.ListItem>(`/sites/${siteId}/lists/${listId}/items`)
            .post<Graph.ListItem>({
              fields: body
            });
          if (!response.id) throw new Error(`No id for item ${response.id}`);
          return { id: response.id };
        }
      })
    };
  }

  delete<L extends List>(list: L) {
    return {
      where: (selector: IdSelector) => {
        const listName = list.meta.id;
        const listId = this.metadata.lists[listName].id;
        const siteId = this.metadata.lists[listName].siteId;
        const keys: Array<unknown> = ['delete', { [list.meta.id]: `/sites/${siteId}/lists/${listId}/items` }];
        const invalidatedKeys = [{ [list.meta.id]: `/sites/${siteId}/lists/${listId}/items` }];

        return {
          keys,
          invalidatedKeys,
          listId,
          siteId,
          execute: async () => {
            await this.client.api(`/sites/${siteId}/lists/${listId}/items/${selector}`).delete();
          }
        };
      }
    };
  }
}
